import React, { useState, useEffect } from 'react';

import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import SuperAdminLayout from '../components/SuperAdminLayout';
import { useSettings } from '../context/SettingsContext';

const SuperAdminSettings = () => {
  const { refreshSettings } = useSettings();
  const [settings, setSettings] = useState({
    systemName: 'Tiket Jini',
    supportEmail: 'support@tiketjini.com',
    supportPhone: '+91-9876543210',
    maintenanceMode: false,
    allowNewRegistrations: true,
    maxTenantsPerPlan: {
      basic: 1,
      premium: 5,
      enterprise: 50
    },
    globalPaymentGateways: {
      payu: { enabled: true, testMode: false },
      icici: { enabled: true, testMode: false },
      idfc: { enabled: false, testMode: true }
    },
    notificationServices: {
      email: {
        enabled: false,
        provider: 'smtp',
        config: {
          host: '',
          port: 587,
          secure: false,
          user: '',
          password: '',
          from: ''
        }
      },
      sms: {
        enabled: false,
        provider: 'twilio',
        config: {
          accountSid: '',
          authToken: '',
          fromNumber: ''
        }
      },
      whatsapp: {
        enabled: false,
        provider: 'twilio',
        config: {
          accountSid: '',
          authToken: '',
          fromNumber: ''
        }
      }
    }
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [gateways, setGateways] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingGateway, setEditingGateway] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'payu',
    config: {},
    isActive: true
  });

  const getConfigFields = (type) => {
    const configs = {
      payu: [
        { key: 'merchantKey', label: 'Merchant Key', type: 'text', required: true },
        { key: 'merchantSalt', label: 'Merchant Salt', type: 'password', required: true }
      ],
      icici: [
        { key: 'merchantId', label: 'Merchant ID', type: 'text', required: true },
        { key: 'accessCode', label: 'Access Code', type: 'text', required: true },
        { key: 'workingKey', label: 'Working Key', type: 'password', required: true }
      ],
      idfc: [
        { key: 'merchantId', label: 'Merchant ID', type: 'text', required: true },
        { key: 'secretKey', label: 'Secret Key', type: 'password', required: true }
      ],
      phonepe: [
        { key: 'merchantId', label: 'Merchant ID', type: 'text', required: true },
        { key: 'saltKey', label: 'Salt Key', type: 'password', required: true },
        { key: 'saltIndex', label: 'Salt Index', type: 'text', required: false },
        { key: 'environment', label: 'Environment', type: 'select', required: true, options: [
          { value: 'uat', label: 'UAT (Testing)' },
          { value: 'production', label: 'Production (Live)' }
        ]}
      ]
    };
    return configs[type] || [];
  };

  const updateConfig = (key, value) => {
    setFormData({
      ...formData,
      config: { ...formData.config, [key]: value }
    });
  };

  useEffect(() => {
    fetchSettings();
    if (activeTab === 'gateways') {
      fetchGateways();
    }
  }, [activeTab]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/super-admin/settings`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.success) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };


  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_BASE_URL}/api/super-admin/settings`, settings, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      refreshSettings();
      alert('Settings saved successfully!');
    } catch (error) {
      alert('Error saving settings: ' + (error.response?.data?.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const toggleMaintenanceMode = async () => {
    const enabled = !settings.maintenanceMode?.enabled;
    const message = 'The system is currently under maintenance. Please try again later.';
    
    if (enabled && !window.confirm('⚠️ Warning: This will prevent all users from accessing the system. Only super admins will be able to log in. Continue?')) {
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/system-settings/maintenance/toggle`, {
        enabled,
        message
      });
      
      setSettings({
        ...settings,
        maintenanceMode: {
          enabled,
          message,
          enabledAt: enabled ? new Date() : null
        }
      });
      
      alert(enabled ? 'Maintenance mode enabled' : 'Maintenance mode disabled');
    } catch (error) {
      alert('Error toggling maintenance mode: ' + (error.response?.data?.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchGateways = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/payment-gateways`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setGateways(response.data.gateways || []);
    } catch (error) {
      console.error('Error fetching gateways:', error);
    }
  };

  const handleGatewaySubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (editingGateway) {
        await axios.put(`${API_BASE_URL}/api/payment-gateways/${editingGateway._id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_BASE_URL}/api/payment-gateways`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      fetchGateways();
      resetGatewayForm();
    } catch (error) {
      alert('Error saving gateway: ' + (error.response?.data?.message || error.message));
    }
  };

  const resetGatewayForm = () => {
    setFormData({ name: '', type: 'payu', config: {}, isActive: true });
    setShowAddForm(false);
    setEditingGateway(null);
  };

  const startEdit = (gateway) => {
    setFormData(gateway);
    setEditingGateway(gateway);
    setShowAddForm(true);
  };

  const handleDeleteGateway = async (id) => {
    if (window.confirm('Are you sure you want to delete this gateway?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API_BASE_URL}/api/payment-gateways/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchGateways();
      } catch (error) {
        alert('Error deleting gateway: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const tabs = [
    { id: 'general', label: '⚙️ General', icon: '⚙️' },
    { id: 'content', label: '📝 Website Content', icon: '📝' },
    { id: 'gateways', label: '🏦 Payment Gateways', icon: '🏦' },
    { id: 'notifications', label: '📧 Notifications', icon: '📧' },
    { id: 'limits', label: '📊 Limits', icon: '📊' },
    { id: 'maintenance', label: '🔧 Maintenance', icon: '🔧' }
  ];

  return (
    <SuperAdminLayout>
      <header style={{
        background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #10b981 100%)',
        color: 'white',
        padding: '16px 20px',
        boxShadow: '0 4px 15px rgba(6, 182, 212, 0.2)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: '700', margin: '0 0 4px 0' }}>System Settings</h1>
            <p style={{ color: 'rgba(255,255,255,0.8)', margin: 0, fontSize: '0.85rem' }}>Configure system preferences</p>
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.2)',
            padding: '6px 12px',
            borderRadius: '8px',
            fontSize: '0.8rem',
            fontWeight: '600'
          }}>
            Settings
          </div>
        </div>
      </header>

      <main style={{ padding: '16px', overflow: 'auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '200px 1fr', gap: '16px' }}>
          {/* Compact Sidebar */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 4px 15px rgba(6, 182, 212, 0.1)',
            border: '1px solid rgba(6, 182, 212, 0.1)',
            height: 'fit-content'
          }}>
            <h3 style={{ margin: '0 0 12px', color: '#1e293b', fontSize: '1rem', fontWeight: '700' }}>Settings</h3>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: activeTab === tab.id ? 'linear-gradient(135deg, #06b6d4, #10b981)' : 'transparent',
                  color: activeTab === tab.id ? 'white' : '#64748b',
                  border: 'none',
                  borderRadius: '6px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  marginBottom: '4px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Compact Content */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 15px rgba(6, 182, 212, 0.1)',
            border: '1px solid rgba(6, 182, 212, 0.1)'
          }}>
            {activeTab === 'general' && (
              <div>
                <h2 style={{ margin: '0 0 16px', color: '#1e293b', fontSize: '1.2rem', fontWeight: '700' }}>General Settings</h2>
                
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.9rem' }}>
                    System Name
                  </label>
                  <input
                    type="text"
                    value={settings.systemName}
                    onChange={(e) => setSettings({...settings, systemName: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '2px solid rgba(6, 182, 212, 0.2)',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Support Email
                  </label>
                  <input
                    type="email"
                    value={settings.supportEmail}
                    onChange={(e) => setSettings({...settings, supportEmail: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Support Phone
                  </label>
                  <input
                    type="tel"
                    value={settings.supportPhone}
                    onChange={(e) => setSettings({...settings, supportPhone: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={settings.allowNewRegistrations}
                      onChange={(e) => setSettings({...settings, allowNewRegistrations: e.target.checked})}
                      style={{ marginRight: '12px' }}
                    />
                    <span style={{ fontWeight: '600' }}>Allow New Tenant Registrations</span>
                  </label>
                </div>
              </div>
            )}

            {activeTab === 'content' && (
              <div>
                <h2 style={{ margin: '0 0 24px', color: '#2d3748' }}>Website Content Settings</h2>
                
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Footer Title
                  </label>
                  <input
                    type="text"
                    defaultValue="Ready for your next adventure?"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Footer Subtitle
                  </label>
                  <textarea
                    defaultValue="Join thousands of happy customers who trust Tiket Jini for their experiences."
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Support Phone
                  </label>
                  <input
                    type="tel"
                    defaultValue="+91-9876543210"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Support Email
                  </label>
                  <input
                    type="email"
                    defaultValue="support@tiketjini.com"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '16px'
                    }}
                  />
                </div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div>
                <h2 style={{ margin: '0 0 24px', color: '#2d3748' }}>Notification Services</h2>
                
                <div style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '24px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                    <span style={{ fontSize: '1.5rem', marginRight: '12px' }}>📧</span>
                    <h3 style={{ margin: 0, color: '#2d3748' }}>Email Service</h3>
                    <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={settings.notificationServices?.email?.enabled || false}
                        onChange={(e) => setSettings({
                          ...settings,
                          notificationServices: {
                            ...settings.notificationServices,
                            email: { ...settings.notificationServices.email, enabled: e.target.checked }
                          }
                        })}
                        style={{ marginRight: '8px' }}
                      />
                      <span>Enabled</span>
                    </label>
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Email Provider</label>
                    <select
                      value={settings.notificationServices?.email?.provider || 'smtp'}
                      onChange={(e) => setSettings({
                        ...settings,
                        notificationServices: {
                          ...settings.notificationServices,
                          email: {
                            ...settings.notificationServices.email,
                            provider: e.target.value,
                            config: {} // Reset config when provider changes
                          }
                        }
                      })}
                      style={{ width: '200px', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }}
                    >
                      <option value="smtp">Custom SMTP</option>
                      <option value="gmail">Gmail</option>
                      <option value="office365">Office 365</option>
                      <option value="aws-ses">AWS SES</option>
                    </select>
                  </div>
                  
                  {/* Custom SMTP Configuration */}
                  {settings.notificationServices?.email?.provider === 'smtp' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>SMTP Host</label>
                        <input
                          type="text"
                          value={settings.notificationServices?.email?.config?.host || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            notificationServices: {
                              ...settings.notificationServices,
                              email: {
                                ...settings.notificationServices.email,
                                config: { ...settings.notificationServices.email.config, host: e.target.value }
                              }
                            }
                          })}
                          placeholder="smtp.example.com"
                          style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '14px' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Port</label>
                        <input
                          type="number"
                          value={settings.notificationServices?.email?.config?.port || 587}
                          onChange={(e) => setSettings({
                            ...settings,
                            notificationServices: {
                              ...settings.notificationServices,
                              email: {
                                ...settings.notificationServices.email,
                                config: { ...settings.notificationServices.email.config, port: parseInt(e.target.value) }
                              }
                            }
                          })}
                          style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '14px' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Username</label>
                        <input
                          type="text"
                          value={settings.notificationServices?.email?.config?.user || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            notificationServices: {
                              ...settings.notificationServices,
                              email: {
                                ...settings.notificationServices.email,
                                config: { ...settings.notificationServices.email.config, user: e.target.value }
                              }
                            }
                          })}
                          style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '14px' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Password</label>
                        <input
                          type="password"
                          value={settings.notificationServices?.email?.config?.password || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            notificationServices: {
                              ...settings.notificationServices,
                              email: {
                                ...settings.notificationServices.email,
                                config: { ...settings.notificationServices.email.config, password: e.target.value }
                              }
                            }
                          })}
                          style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '14px' }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Gmail Configuration */}
                  {settings.notificationServices?.email?.provider === 'gmail' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Gmail Address</label>
                        <input
                          type="email"
                          value={settings.notificationServices?.email?.config?.user || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            notificationServices: {
                              ...settings.notificationServices,
                              email: {
                                ...settings.notificationServices.email,
                                config: { 
                                  ...settings.notificationServices.email.config, 
                                  user: e.target.value,
                                  host: 'smtp.gmail.com',
                                  port: 587,
                                  secure: false
                                }
                              }
                            }
                          })}
                          placeholder="your-email@gmail.com"
                          style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '14px' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>App Password</label>
                        <input
                          type="password"
                          value={settings.notificationServices?.email?.config?.password || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            notificationServices: {
                              ...settings.notificationServices,
                              email: {
                                ...settings.notificationServices.email,
                                config: { ...settings.notificationServices.email.config, password: e.target.value }
                              }
                            }
                          })}
                          placeholder="16-character app password"
                          style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '14px' }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Office 365 Configuration */}
                  {settings.notificationServices?.email?.provider === 'office365' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Office 365 Email</label>
                        <input
                          type="email"
                          value={settings.notificationServices?.email?.config?.user || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            notificationServices: {
                              ...settings.notificationServices,
                              email: {
                                ...settings.notificationServices.email,
                                config: { 
                                  ...settings.notificationServices.email.config, 
                                  user: e.target.value,
                                  host: 'smtp.office365.com',
                                  port: 587,
                                  secure: false
                                }
                              }
                            }
                          })}
                          placeholder="your-email@company.com"
                          style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '14px' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Password</label>
                        <input
                          type="password"
                          value={settings.notificationServices?.email?.config?.password || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            notificationServices: {
                              ...settings.notificationServices,
                              email: {
                                ...settings.notificationServices.email,
                                config: { ...settings.notificationServices.email.config, password: e.target.value }
                              }
                            }
                          })}
                          style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '14px' }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* AWS SES Configuration */}
                  {settings.notificationServices?.email?.provider === 'aws-ses' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>AWS Region</label>
                        <select
                          value={settings.notificationServices?.email?.config?.region || 'us-east-1'}
                          onChange={(e) => setSettings({
                            ...settings,
                            notificationServices: {
                              ...settings.notificationServices,
                              email: {
                                ...settings.notificationServices.email,
                                config: { ...settings.notificationServices.email.config, region: e.target.value }
                              }
                            }
                          })}
                          style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '14px' }}
                        >
                          <option value="us-east-1">US East (N. Virginia)</option>
                          <option value="us-west-2">US West (Oregon)</option>
                          <option value="eu-west-1">Europe (Ireland)</option>
                          <option value="ap-south-1">Asia Pacific (Mumbai)</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>From Email</label>
                        <input
                          type="email"
                          value={settings.notificationServices?.email?.config?.from || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            notificationServices: {
                              ...settings.notificationServices,
                              email: {
                                ...settings.notificationServices.email,
                                config: { ...settings.notificationServices.email.config, from: e.target.value }
                              }
                            }
                          })}
                          placeholder="noreply@yourdomain.com"
                          style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '14px' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Access Key ID</label>
                        <input
                          type="text"
                          value={settings.notificationServices?.email?.config?.accessKeyId || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            notificationServices: {
                              ...settings.notificationServices,
                              email: {
                                ...settings.notificationServices.email,
                                config: { ...settings.notificationServices.email.config, accessKeyId: e.target.value }
                              }
                            }
                          })}
                          style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '14px' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Secret Access Key</label>
                        <input
                          type="password"
                          value={settings.notificationServices?.email?.config?.secretAccessKey || ''}
                          onChange={(e) => setSettings({
                            ...settings,
                            notificationServices: {
                              ...settings.notificationServices,
                              email: {
                                ...settings.notificationServices.email,
                                config: { ...settings.notificationServices.email.config, secretAccessKey: e.target.value }
                              }
                            }
                          })}
                          style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '14px' }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Common From Email field for non-AWS providers */}
                  {settings.notificationServices?.email?.provider !== 'aws-ses' && (
                    <div style={{ marginTop: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>From Email</label>
                      <input
                        type="email"
                        value={settings.notificationServices?.email?.config?.from || ''}
                        onChange={(e) => setSettings({
                          ...settings,
                          notificationServices: {
                            ...settings.notificationServices,
                            email: {
                              ...settings.notificationServices.email,
                              config: { ...settings.notificationServices.email.config, from: e.target.value }
                            }
                          }
                        })}
                        placeholder="noreply@yourdomain.com"
                        style={{ width: '300px', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '14px' }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'limits' && (
              <div>
                <h2 style={{ margin: '0 0 24px', color: '#2d3748' }}>System Limits</h2>
                
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ margin: '0 0 16px', color: '#4a5568' }}>Max Tenants Per Plan</h3>
                  
                  {Object.entries(settings.maxTenantsPerPlan).map(([plan, limit]) => (
                    <div key={plan} style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', textTransform: 'capitalize' }}>
                        {plan} Plan
                      </label>
                      <input
                        type="number"
                        value={limit}
                        onChange={(e) => setSettings({
                          ...settings,
                          maxTenantsPerPlan: {
                            ...settings.maxTenantsPerPlan,
                            [plan]: parseInt(e.target.value)
                          }
                        })}
                        style={{
                          width: '200px',
                          padding: '12px',
                          border: '2px solid #e2e8f0',
                          borderRadius: '8px',
                          fontSize: '16px'
                        }}
                        min="1"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gateways' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <h2 style={{ margin: '0', color: '#2d3748' }}>Payment Gateway Management</h2>
                  <button
                    onClick={() => setShowAddForm(true)}
                    style={{
                      background: 'linear-gradient(135deg, #06b6d4, #10b981)',
                      color: 'white',
                      border: 'none',
                      padding: '10px 16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    + Add Gateway
                  </button>
                </div>

                {showAddForm && (
                  <div style={{
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '24px'
                  }}>
                    <h3 style={{ margin: '0 0 16px', fontSize: '1.1rem', fontWeight: '700' }}>
                      {editingGateway ? 'Edit Gateway' : 'Add New Gateway'}
                    </h3>
                    <form onSubmit={handleGatewaySubmit}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                        <input
                          type="text"
                          placeholder="Gateway Name"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          required
                          style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }}
                        />
                        <select
                          value={formData.type}
                          onChange={(e) => setFormData({...formData, type: e.target.value, config: {}})}
                          style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }}
                        >
                          <option value="payu">PayU</option>
                          <option value="icici">ICICI CCAvenue</option>
                          <option value="idfc">IDFC Bank</option>
                          <option value="phonepe">PhonePe</option>
                        </select>
                      </div>
                      
                      <div style={{ marginBottom: '16px' }}>
                        <h4 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: '600' }}>Configuration</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                          {getConfigFields(formData.type).map(field => (
                            <div key={field.key}>
                              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '600' }}>
                                {field.label} {field.required && <span style={{ color: '#ef4444' }}>*</span>}
                              </label>
                              {field.type === 'select' ? (
                                <select
                                  value={formData.config[field.key] || (field.key === 'environment' ? 'uat' : '')}
                                  onChange={(e) => updateConfig(field.key, e.target.value)}
                                  required={field.required}
                                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }}
                                >
                                  {field.options?.map(option => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type={field.type}
                                  value={formData.config[field.key] || ''}
                                  onChange={(e) => updateConfig(field.key, e.target.value)}
                                  required={field.required}
                                  placeholder={field.key === 'saltIndex' ? 'Default: 1' : ''}
                                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={formData.isActive}
                            onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                            style={{ marginRight: '8px' }}
                          />
                          Active
                        </label>
                        <button
                          type="submit"
                          style={{
                            background: 'linear-gradient(135deg, #06b6d4, #10b981)',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          {editingGateway ? 'Update' : 'Add'} Gateway
                        </button>
                        <button
                          type="button"
                          onClick={resetGatewayForm}
                          style={{
                            background: '#f1f5f9',
                            color: '#64748b',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                  {gateways.map(gateway => (
                    <div key={gateway._id} style={{
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      padding: '20px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                        <div>
                          <h3 style={{ margin: '0', fontSize: '1.1rem', fontWeight: '700' }}>{gateway.name}</h3>
                          <p style={{ margin: '4px 0', color: '#64748b', fontSize: '14px', textTransform: 'uppercase' }}>{gateway.type}</p>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => startEdit(gateway)}
                            style={{
                              background: '#eff6ff',
                              color: '#2563eb',
                              border: 'none',
                              padding: '6px 10px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer'
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteGateway(gateway._id)}
                            style={{
                              background: '#fef2f2',
                              color: '#dc2626',
                              border: 'none',
                              padding: '6px 10px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer'
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      
                      <div style={{
                        padding: '8px 12px',
                        background: gateway.isActive ? '#f0fdf4' : '#fef2f2',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: gateway.isActive ? '#166534' : '#dc2626'
                      }}>
                        {gateway.isActive ? '✓ Active' : '✗ Inactive'}
                      </div>
                    </div>
                  ))}
                </div>

                {gateways.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '16px' }}>💳</div>
                    <h3 style={{ margin: '0 0 8px', fontSize: '1.2rem' }}>No Payment Gateways</h3>
                    <p style={{ margin: '0 0 16px' }}>Add your first payment gateway to get started</p>
                    <button
                      onClick={() => setShowAddForm(true)}
                      style={{
                        background: 'linear-gradient(135deg, #06b6d4, #10b981)',
                        color: 'white',
                        border: 'none',
                        padding: '10px 16px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Add Gateway
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'maintenance' && (
              <div>
                <h2 style={{ margin: '0 0 24px', color: '#2d3748' }}>Maintenance Mode</h2>
                
                <div style={{
                  background: settings.maintenanceMode?.enabled ? '#fed7d7' : '#f0fff4',
                  border: `1px solid ${settings.maintenanceMode?.enabled ? '#feb2b2' : '#9ae6b4'}`,
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '24px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{ fontSize: '2rem', marginRight: '16px' }}>
                        {settings.maintenanceMode?.enabled ? '🚨' : '✅'}
                      </div>
                      <div>
                        <h3 style={{ margin: 0, color: settings.maintenanceMode?.enabled ? '#742a2a' : '#22543d' }}>
                          {settings.maintenanceMode?.enabled ? 'Maintenance Mode Active' : 'System Operational'}
                        </h3>
                        <p style={{ margin: '4px 0 0', color: settings.maintenanceMode?.enabled ? '#742a2a' : '#22543d' }}>
                          {settings.maintenanceMode?.enabled 
                            ? 'The system is currently under maintenance. Users cannot access the platform.'
                            : 'All systems are running normally.'
                          }
                        </p>
                      </div>
                    </div>
                    
                    <button
                      onClick={toggleMaintenanceMode}
                      disabled={loading}
                      style={{
                        background: settings.maintenanceMode?.enabled ? '#48bb78' : '#e53e3e',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: loading ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {settings.maintenanceMode?.enabled ? 'Disable' : 'Enable'}
                    </button>
                  </div>
                  
                  {settings.maintenanceMode?.enabled && settings.maintenanceMode.enabledAt && (
                    <div style={{ fontSize: '12px', color: '#742a2a' }}>
                      Enabled on: {new Date(settings.maintenanceMode.enabledAt).toLocaleString()}
                    </div>
                  )}
                </div>

                {!settings.maintenanceMode?.enabled && (
                  <div style={{
                    background: '#fff5f5',
                    border: '1px solid #fed7d7',
                    borderRadius: '8px',
                    padding: '16px'
                  }}>
                    <p style={{ margin: 0, color: '#742a2a', fontSize: '14px' }}>
                      ⚠️ <strong>Warning:</strong> Enabling maintenance mode will prevent all users from accessing the system. 
                      Only super admins will be able to log in.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Compact Save Button */}
            <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid rgba(6, 182, 212, 0.2)' }}>
              <button
                onClick={handleSaveSettings}
                disabled={loading}
                style={{
                  background: loading ? '#a0aec0' : 'linear-gradient(135deg, #06b6d4, #10b981)',
                  color: 'white',
                  border: 'none',
                  padding: '8px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 15px rgba(6, 182, 212, 0.3)'
                }}
              >
                {loading ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      </main>
    </SuperAdminLayout>
  );
};

export default SuperAdminSettings;